import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import commissionMessagesService from '../../services/commissionMessagesService';
import './CommissionMessagingModal.css';

const CommissionMessagingModal = ({ commission, onClose, onMessageSent }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (commission?.id) {
      fetchMessages();
      // Mark messages as read when modal opens
      commissionMessagesService.markAsRead(commission.id).catch(console.error);
    }
  }, [commission?.id]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const data = await commissionMessagesService.getMessages(commission.id);
      setMessages(data);
    } catch (error) {
      console.error('Error fetching messages:', error);
      setError('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;

    try {
      setSending(true);
      setError('');
      
      await commissionMessagesService.sendAgentReply(commission.id, reply.trim());
      
      // Refresh messages
      await fetchMessages();
      setReply('');
      
      // Notify parent component
      if (onMessageSent) {
        onMessageSent();
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      setError('Failed to send reply. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <div className="messaging-modal-overlay" onClick={onClose}>
      <div className="messaging-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="messaging-modal-header">
          <h2>Commission Messages</h2>
          <button className="close-button" onClick={onClose}>
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Commission Summary */}
        <div className="commission-summary">
          <h3>Commission Details</h3>
          <div className="summary-grid">
            <div className="summary-item">
              <span className="summary-label">Reservation #:</span>
              <span className="summary-value">{commission.reservation_number}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Guest:</span>
              <span className="summary-value">{commission.guest_first_name} {commission.guest_last_name}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Supplier:</span>
              <span className="summary-value">{commission.supplier}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Travel Date:</span>
              <span className="summary-value">{new Date(commission.travel_date).toLocaleDateString()}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Gross Commission:</span>
              <span className="summary-value">{formatCurrency(commission.gross_commission)}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Agent Commission:</span>
              <span className="summary-value">{formatCurrency(commission.agent_commission)}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Status:</span>
              <span className={`status-badge status-${commission.status}`}>
                {commission.status.charAt(0).toUpperCase() + commission.status.slice(1).replace('_', ' ')}
              </span>
            </div>
          </div>
        </div>

        {/* Messages Section */}
        <div className="messages-section">
          <h3>Message History</h3>
          
          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading messages...</p>
            </div>
          ) : messages.length === 0 ? (
            <div className="empty-messages">
              <p>No messages yet.</p>
            </div>
          ) : (
            <div className="messages-list">
              {messages.map((message) => (
                <div 
                  key={message.id} 
                  className={`message ${message.sender_type === 'agent' ? 'message-agent' : 'message-admin'}`}
                >
                  <div className="message-header">
                    <span className="message-sender">
                      {message.sender_type === 'agent' ? 'You' : message.sender_name}
                    </span>
                    <span className="message-time">{formatDate(message.created_at)}</span>
                  </div>
                  <div className="message-content">
                    {message.message}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Reply Section */}
        {commission.status === 'needs_reply' && (
          <div className="reply-section">
            <h3>Send Reply</h3>
            <form onSubmit={handleSendReply}>
              <div className="reply-input-group">
                <textarea
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder="Type your reply here..."
                  rows="4"
                  className="reply-textarea"
                  disabled={sending}
                />
              </div>
              
              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}
              
              <div className="reply-actions">
                <button 
                  type="button" 
                  onClick={onClose}
                  className="btn btn-secondary"
                  disabled={sending}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={sending || !reply.trim()}
                >
                  {sending ? 'Sending...' : 'Send Reply'}
                </button>
              </div>
            </form>
          </div>
        )}

        {commission.status !== 'needs_reply' && (
          <div className="no-reply-needed">
            <p className="info-message">
              No reply needed at this time. This commission is currently in "{commission.status}" status.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommissionMessagingModal;
